import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Button, Result, Spin } from "antd";
import { useTranslation } from "react-i18next";
import { isDevAuthBypass } from "@/lib/devAuth";
import { ensureDeveloperProfile } from "@/lib/loadDeveloperProfile";
import { useAuthStore } from "@/store/useAuthStore";
import { useUserStore } from "@/store/useUserStore";
import { useRuntimeStore } from "@/store/useRuntimeStore";
import VersionFlowHost from "@/pages/app-detail/versions/VersionFlowHost";

/** 未登录时浏览器进 /login；客户端等宿主 JWT，不跳登录页。本地 VITE_DEV_* 旁路直接放行。 */
function AuthGuard() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const ready = useAuthStore((s) => s.ready);
  const token = useAuthStore((s) => s.token);
  const hostAuthFailed = useAuthStore((s) => s.hostAuthFailed);
  const refreshFromHost = useAuthStore((s) => s.refreshFromHost);
  const isClient = useRuntimeStore((s) => s.isClient);
  const allowed = isDevAuthBypass() || Boolean(token);
  const authed = useUserStore((s) => s.isAuthenticated);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    if (!allowed || !authed) return;
    ensureDeveloperProfile();
  }, [allowed, authed]);

  useEffect(() => {
    if (!ready || allowed || isClient) return;
    const from = `${location.pathname}${location.search}`;
    navigate(`/login?from=${encodeURIComponent(from)}`, { replace: true });
  }, [allowed, isClient, location.pathname, location.search, navigate, ready]);

  if (!ready) {
    return (
      <div className="flex min-h-full min-h-[100vh] flex-col items-center justify-center gap-3 bg-[var(--page)]">
        <Spin size="large" />
        <span className="text-sm text-[var(--fg-muted,#64748b)]">
          {t("login.loading")}
        </span>
      </div>
    );
  }

  if (!allowed && isClient) {
    if (hostAuthFailed) {
      return (
        <div className="flex min-h-full min-h-[100vh] items-center justify-center bg-[var(--page)] p-6">
          <Result
            status="warning"
            title={t("login.hostAuthFailed")}
            subTitle={t("login.hostAuthFailedHint")}
            extra={
              <Button
                type="primary"
                loading={retrying}
                onClick={() => {
                  setRetrying(true);
                  void refreshFromHost().finally(() => setRetrying(false));
                }}
              >
                {t("login.retry")}
              </Button>
            }
          />
        </div>
      );
    }
    return (
      <div className="flex min-h-full min-h-[100vh] flex-col items-center justify-center gap-3 bg-[var(--page)]">
        <Spin size="large" />
        <span className="text-sm text-[var(--fg-muted,#64748b)]">
          {t("login.waitingHost")}
        </span>
      </div>
    );
  }

  if (!allowed) return null;

  return (
    <>
      <Outlet />
      <VersionFlowHost />
    </>
  );
}

export default AuthGuard;
